
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {computeMidpoint} from "./midpoint.js";

function clamp01(x){
	return Math.max(0, Math.min(1, x));
}

// closest point on segment [a,b] to point p, clamped to the segment
export function closestPointOnSegment(p, a, b){
	const ab = b.clone().sub(a);
	const lenSq = ab.lengthSq();
	const t = lenSq > 1e-12 ? clamp01(p.clone().sub(a).dot(ab) / lenSq) : 0;
	const point = a.clone().add(ab.multiplyScalar(t));
	return {point, t};
}

// closest point anywhere along a polyline (not just at its vertices) to point p
export function closestPointOnPolyline(p, points){
	let best = null;

	for(let i = 0; i < points.length - 1; i++){
		const {point, t} = closestPointOnSegment(p, points[i], points[i + 1]);
		const distSq = point.distanceToSquared(p);

		if(!best || distSq < best.distSq){
			best = {point, t, segmentIndex: i, distSq};
		}
	}

	return best;
}

// Screen-space tracking for the Midline connector. Deliberately NOT driven
// by a 3D point-cloud raycast: the point cloud is sparse/gappy, so "closest
// 3D point to the raycast hit" freezes whenever the cursor drifts over a gap
// with no hit, which reads as snapping/sticking at whichever vertex it last
// found. Projecting the polyline to screen space and following the mouse's
// raw pixel position there is continuous regardless of point-cloud coverage
// - the cursor is the primary driver, exactly as required.
export function projectToScreen(worldPos, camera, width, height){
	const ndc = worldPos.clone().project(camera);
	return {x: (ndc.x + 1) * width / 2, y: (-ndc.y + 1) * height / 2};
}

export function projectPolylineToScreen(points, camera, width, height){
	return points.map(p => projectToScreen(p, camera, width, height));
}

export function closestTOnScreenSegment(mouse, a, b){
	const abx = b.x - a.x;
	const aby = b.y - a.y;
	const lenSq = abx * abx + aby * aby;
	return lenSq > 1e-9 ? clamp01(((mouse.x - a.x) * abx + (mouse.y - a.y) * aby) / lenSq) : 0;
}

// Finds the continuous position on a polyline whose *screen-space*
// projection is closest to the mouse's pixel position, preferring to stay on
// or near a walker's remembered current segment instead of always doing a
// fresh global search - this is what makes the connector glide across an
// intermediate vertex from one segment to the next instead of flickering
// between whichever segment happens to be globally nearest on a given frame
// (adjacent segments share their boundary vertex, so a hand-off between them
// is inherently seamless), per the "hysteresis picks the segment, never the
// position" requirement.
//
// walkerState is a plain {segmentIndex} object the caller owns and passes
// back in on every call; pass a fresh {} to force a full/global search
// (e.g. the very first update, or after a big camera/mouse jump).
export function walkPolylineScreen(mouse, points, screenPoints, walkerState = {}){
	const n = points.length;
	if(n < 2) return null;

	const evalSegment = (i) => {
		const t = closestTOnScreenSegment(mouse, screenPoints[i], screenPoints[i + 1]);
		const sx = screenPoints[i].x + t * (screenPoints[i + 1].x - screenPoints[i].x);
		const sy = screenPoints[i].y + t * (screenPoints[i + 1].y - screenPoints[i].y);
		const dx = mouse.x - sx;
		const dy = mouse.y - sy;
		const point = points[i].clone().lerp(points[i + 1], t);

		return {segmentIndex: i, t, point, distSq: dx * dx + dy * dy};
	};

	const globalSearch = () => {
		let best = null;
		for(let i = 0; i < n - 1; i++){
			const r = evalSegment(i);
			if(!best || r.distSq < best.distSq) best = r;
		}
		if(best) walkerState.segmentIndex = best.segmentIndex;
		return best;
	};

	if(walkerState.segmentIndex == null){
		return globalSearch();
	}

	const current = Math.max(0, Math.min(n - 2, walkerState.segmentIndex));
	let best = null;

	for(const i of [current - 1, current, current + 1]){
		if(i < 0 || i >= n - 1) continue;

		const r = evalSegment(i);
		if(!best || r.distSq < best.distSq) best = r;
	}

	// stay local (stable, non-flickering) for normal, continuous mouse
	// movement; only fall back to a full search if the cursor is clearly no
	// longer anywhere near the current neighbourhood in screen space (a real
	// jump - camera cut, click elsewhere - not just crossing a vertex)
	const localSegLengthPx = Math.hypot(
		screenPoints[current + 1].x - screenPoints[current].x,
		screenPoints[current + 1].y - screenPoints[current].y);
	const escapeDistancePx = Math.max(localSegLengthPx * 3, 80);

	if(Math.sqrt(best.distSq) > escapeDistancePx){
		return globalSearch();
	}

	walkerState.segmentIndex = best.segmentIndex;
	return best;
}

// Vector3-friendly wrapper around the single shared computeMidpoint() -
// used for the live 3D preview; the same underlying math also produces the
// feature's stored coordinates (see midpointTool.js/midlineTool.js).
export function midpointOf(a, b){
	const [x, y, z] = computeMidpoint([a.x, a.y, a.z], [b.x, b.y, b.z]);
	return new THREE.Vector3(x, y, z);
}
